import { useQuery } from "@apollo/client";
import AccountStatus from "enums/AccountStatus";
import AccountType from "enums/AccountType";
import ApplicationType from "enums/ApplicationType";
import CardIssuer from "enums/CardIssuer";
import type { CardProgramCapabilities } from "hooks/useCardPrograms";
import {
  GET_BANK_ACCOUNT_BALANCES,
  GET_LIST_BANK_ACCOUNTS,
} from "graphql/company";
import { useEffect, useMemo } from "react";
import { formatCashFull } from "utils/formatCash";

interface BalanceAmount {
  amount?: number;
  currency?: string;
}

interface BankAccountBalance {
  available?: BalanceAmount;
  creditLimit?: BalanceAmount;
  outstanding?: BalanceAmount;
}

export interface CardProgramSummary {
  id: string;
  issuer?: CardIssuer;
  nickname?: string;
  isDefault?: boolean;
  capabilities?: CardProgramCapabilities;
}

export interface BankAccount {
  id: string;
  accountId?: string;
  last4?: string;
  bankName?: string;
  nickname?: string;
  accountNumber?: string;
  routingNumber?: string;
  accountHolderName?: string;
  bankAddress?: string;
  availableAmount?: string;
  creditLimit?: string;
  outstanding?: string;
  /** The outstanding balance as a number, for a field that has to hold one. */
  outstandingAmount?: number;
  status: string;
  accountType: AccountType;
  applicationType?: ApplicationType;
  microDepositUrl?: string;
  /** Set on the company's own accounts, absent on linked external banks. */
  cardProgram?: CardProgramSummary;
  /** The issuer connection this account moves money through. */
  connectionId?: string;
  /**
   * The account's own currency, off its balance. The formatted `*Amount`
   * strings already use it; this exposes it for callers that format their own
   * numbers, so they do not fall back to `formatCashFull`'s dollars.
   */
  currency?: string;
}

type UseGetBankAccountsParams = {
  accountTypes: AccountType[];
  status?: AccountStatus[];
  skip?: boolean;
  withBalances?: boolean;
  afterFetch?: (accounts: BankAccount[]) => void;
};

// Return shape of the hook, exported so a parent can fetch once and pass the
// result down to children (which self-fetch only when this prop is absent).
export interface UseGetBankAccountsResult {
  allAccounts: BankAccount[];
  internalAccounts: BankAccount[];
  externalAccounts: BankAccount[];
  combinedBalances: string;
  /** The same total the formatted `combinedBalances` shows, unformatted. */
  combinedBalanceAmount: number;
  isLoading: boolean;
  loadingBalances: boolean;
  refetchAllAccounts: () => void;
}

const useGetBankAccounts = ({
  accountTypes,
  status = [AccountStatus.ACTIVE, AccountStatus.MICRODEPOSITS_PENDING],
  skip = false,
  withBalances = true,
  afterFetch,
}: UseGetBankAccountsParams): UseGetBankAccountsResult => {
  const variables = { status, types: accountTypes };

  // Account metadata: fast, indexed lookup only. Renders immediately.
  const {
    data: bankAccountsData,
    loading: loadingBankAccounts,
    refetch: refetchBankAccounts,
  } = useQuery(GET_LIST_BANK_ACCOUNTS, {
    variables,
    fetchPolicy: "cache-first",
    skip: !accountTypes || skip,
  });

  // Balances: fetched separately so slow live balance lookups don't block the
  // accounts list. Merges into the same normalized entities via account id.
  const {
    data: balancesData,
    loading: loadingBalances,
    refetch: refetchBalances,
  } = useQuery(GET_BANK_ACCOUNT_BALANCES, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: !accountTypes || skip || !withBalances,
  });

  const balanceById = useMemo(() => {
    const map: Record<string, BankAccountBalance> = {};
    (balancesData?.getListBankAccounts || []).forEach(
      (account: { id?: string; balance?: BankAccountBalance }) => {
        if (account?.id) map[account.id] = account.balance ?? {};
      },
    );
    return map;
  }, [balancesData]);

  // Every financial account reports its own balance, so the total is the sum.
  const { allAccounts, combinedBalances } = useMemo(() => {
    let combined = 0;
    const accounts: BankAccount[] = (
      bankAccountsData?.getListBankAccounts || []
    ).map((account: any) => {
      const balance = balanceById[account.id];
      if (balance?.available?.amount) {
        combined += balance.available.amount;
      }
      return {
        id: account.id,
        last4: account.last4 ?? account.details?.accountNumber?.slice(-4),
        bankName: account.details?.bankName || account.bankName,
        // The issuer keeps the name an account opened with, so a card program's own name wins.
        nickname: account.cardProgram?.nickname || account.details?.nickname,
        accountNumber: account.details?.accountNumber,
        routingNumber: account.details?.routingNumber,
        accountHolderName: account.details?.accountHolderName,
        bankAddress: account.details?.bankAddress,
        availableAmount:
          balance?.available?.amount != null
            ? formatCashFull(
                balance.available.amount,
                balance.available.currency,
              )
            : undefined,
        creditLimit:
          balance?.creditLimit?.amount != null
            ? formatCashFull(
                balance.creditLimit.amount,
                balance.creditLimit.currency,
              )
            : undefined,
        outstanding:
          balance?.outstanding?.amount != null
            ? formatCashFull(
                balance.outstanding.amount,
                balance.outstanding.currency,
              )
            : undefined,
        outstandingAmount: balance?.outstanding?.amount ?? undefined,
        currency: balance?.available?.currency,
        status: account.status,
        accountType: account.accountType,
        applicationType: account.applicationType,
        microDepositUrl: account.microDepositUrl,
        cardProgram: account.cardProgram ?? undefined,
        connectionId: account.connectionId ?? undefined,
      };
    });
    return { allAccounts: accounts, combinedBalances: combined };
  }, [bankAccountsData, balanceById]);

  const internalAccounts = useMemo(
    () =>
      allAccounts.filter(
        (account) => account.accountType === AccountType.FINANCIAL_ACCOUNT,
      ),
    [allAccounts],
  );
  const externalAccounts = useMemo(
    () =>
      allAccounts.filter(
        (account) =>
          account.accountType === AccountType.PLAID_EXTERNAL_CARD_ACCOUNT ||
          account.accountType === AccountType.BANK,
      ),
    [allAccounts],
  );

  useEffect(() => {
    if (afterFetch && allAccounts.length > 0) {
      afterFetch(allAccounts);
    }
  }, [afterFetch, allAccounts]);

  const refetchAllAccounts = () => {
    refetchBankAccounts();
    if (withBalances) refetchBalances();
  };

  const isLoading = loadingBankAccounts;

  return {
    allAccounts,
    internalAccounts,
    externalAccounts,
    combinedBalances: formatCashFull(combinedBalances),
    combinedBalanceAmount: combinedBalances,
    isLoading,
    loadingBalances,
    refetchAllAccounts,
  };
};

/**
 * Uses a parent-provided accounts result when present, otherwise self-fetches.
 * The self-fetch is skipped while `provided` is set, so a shared parent fetch
 * isn't duplicated by children.
 */
export const useResolvedBankAccounts = (
  provided: UseGetBankAccountsResult | undefined,
  params: UseGetBankAccountsParams,
): UseGetBankAccountsResult => {
  const selfFetched = useGetBankAccounts({
    ...params,
    skip: params.skip || !!provided,
  });
  return provided ?? selfFetched;
};

export default useGetBankAccounts;
